// AI agent voice campaigns — real, backed by /v1/voice-campaigns.
//
// cpass is the control surface; the voicex runtime places the calls. So the
// counts shown here are the runtime's own, never recomputed locally, and a
// campaign's status is whatever the runtime reports.
//
// Replaces the previous AICampaignCreation wizard, which collected a full
// configuration and submitted it nowhere.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Megaphone, Plus, RefreshCw, AlertCircle, Play, Pause, Users, Loader2,
  ChevronDown, ChevronRight, ArrowUpRight,
} from 'lucide-react';
import {
  voiceCampaignsService, VoiceCampaign, BusinessNumber, RetryConfig, CampaignEngineInfo,
} from 'api/services/voiceCampaigns.service';
import { aiAgentsService, AiAgent } from 'api/services/aiAgents.service';
import CampaignDetail from './CampaignDetail';
import QueueRecipientsModal from './QueueRecipientsModal';
import {
  findCampaign, lifecycleStates, formatCounter, isEnforced, notEnforcedNote,
} from './campaignDetailRules';
import { CallCategorySelect, traiComplianceOn } from 'components/Compliance/CallCategoryField';
import type { A2pCategory } from 'api/services/traiCompliance.service';

/**
 * Blank = "don't send it", so the runtime keeps its own default rather than
 * having it overwritten by a guess from this form.
 */
const num = (v: string): number | undefined => (v.trim() === '' ? undefined : Number(v));

const badge = (status?: string) => {
  const s = (status || '').toUpperCase();
  if (s === 'ACTIVE') return 'bg-green-100 text-green-700';
  if (s === 'PAUSED') return 'bg-amber-100 text-amber-700';
  if (s === 'COMPLETED') return 'bg-blue-100 text-blue-700';
  if (s === 'ARCHIVED') return 'bg-gray-100 text-gray-600';
  return 'bg-gray-100 text-gray-600'; // DRAFT
};

const AIVoiceCampaigns: React.FC = () => {
  const [rows, setRows] = useState<VoiceCampaign[]>([]);
  const [agents, setAgents] = useState<AiAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [numbers, setNumbers] = useState<BusinessNumber[]>([]);
  const [numbersNote, setNumbersNote] = useState<string | null>(null);
  const [showRetry, setShowRetry] = useState(false);
  const BLANK_FORM = {
    name: '', description: '', agent_id: '', caller_id: '', calls_per_minute: '',
    max_concurrent_calls: '', start_date: '', end_date: '', welcome_message: '',
  };
  const [form, setForm] = useState(BLANK_FORM);
  // Left empty by default so the runtime's own retry defaults apply untouched.
  const [retry, setRetry] = useState<Record<string, string>>({
    max_attempts: '', retry_strategy: '', base_retry_delay_minutes: '', backoff_factor: '',
    max_retry_delay_minutes: '', max_retries_per_day: '', jitter_factor: '',
    final_failure_action: '', failure_webhook_url: '', failure_transfer_number: '',
  });
  const [respectHours, setRespectHours] = useState<boolean | null>(null);
  // TRAI call category: only behind the trai-compliance build flag; empty sends nothing.
  const [category, setCategory] = useState<'' | A2pCategory>('');

  const [recipientsFor, setRecipientsFor] = useState<VoiceCampaign | null>(null);

  /** The campaign being looked at up close. An opaque id, never coerced. */
  const [detailId, setDetailId] = useState<string | null>(null);

  /**
   * What this organisation's engine will accept and then ignore.
   *
   * Read at runtime because it is engine-dependent — the cpass dialler stores a
   * rate limit it never honours, and hardcoding that list here would go stale
   * the moment an engine learns to honour something.
   */
  const [engine, setEngine] = useState<CampaignEngineInfo | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const res = await voiceCampaignsService.list();
    if (res.success) setRows(res.data || []);
    else setError(res.error || 'Failed to load campaigns');
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    aiAgentsService.list().then(r => { if (r.success) setAgents(r.data || []); });
    // Not fatal: without allocated numbers the caller ID can still be typed.
    voiceCampaignsService.numbers().then(r => {
      if (r.success) setNumbers(r.data || []);
      else setNumbersNote(r.error || 'Could not load numbers from the runtime');
    });
    // Not fatal either: an unknown engine means every control stays enabled,
    // because greying out a working field over a failed lookup is its own lie.
    voiceCampaignsService.engine().then(r => { if (r.success) setEngine(r.data || null); });
  }, [load]);

  const idOf = (c: VoiceCampaign) => c.id || '';

  const submitCreate = async () => {
    if (!form.name.trim()) { setError('A campaign name is required'); return; }

    // Only include retry keys the user actually set — see RetryConfig.
    const r: RetryConfig = {};
    if (retry.max_attempts) r.max_attempts = Number(retry.max_attempts);
    if (retry.retry_strategy) r.retry_strategy = retry.retry_strategy as RetryConfig['retry_strategy'];
    if (retry.base_retry_delay_minutes) r.base_retry_delay_minutes = Number(retry.base_retry_delay_minutes);
    if (retry.backoff_factor) r.backoff_factor = Number(retry.backoff_factor);
    if (retry.max_retry_delay_minutes) r.max_retry_delay_minutes = Number(retry.max_retry_delay_minutes);
    if (retry.max_retries_per_day) r.max_retries_per_day = Number(retry.max_retries_per_day);
    if (retry.jitter_factor) r.jitter_factor = Number(retry.jitter_factor);
    if (retry.final_failure_action) r.final_failure_action = retry.final_failure_action as RetryConfig['final_failure_action'];
    if (retry.failure_webhook_url) r.failure_webhook_url = retry.failure_webhook_url;
    if (retry.failure_transfer_number) r.failure_transfer_number = retry.failure_transfer_number;
    if (respectHours !== null) r.respect_business_hours = respectHours;

    setBusy('create');
    const res = await voiceCampaignsService.create({
      name: form.name.trim(),
      description: form.description || undefined,
      agent_id: form.agent_id || undefined,
      caller_id: form.caller_id || undefined,
      calls_per_minute: num(form.calls_per_minute),
      max_concurrent_calls: num(form.max_concurrent_calls),
      start_date: form.start_date ? new Date(form.start_date).toISOString() : undefined,
      end_date: form.end_date ? new Date(form.end_date).toISOString() : undefined,
      welcome_message: form.welcome_message || undefined,
      retry: Object.keys(r).length ? r : undefined,
      category: traiComplianceOn() && category ? category : undefined,
    });
    setBusy(null);
    if (!res.success) { setError(res.error || 'Create failed'); return; }
    setCreating(false);
    setForm(BLANK_FORM);
    setRespectHours(null);
    setCategory('');
    setShowRetry(false);
    load();
  };

  const control = async (c: VoiceCampaign, action: 'start' | 'pause' | 'resume') => {
    const id = idOf(c); if (!id) return;
    setBusy(id + action); setError(null);
    const res = await voiceCampaignsService[action](id);
    setBusy(null);
    if (!res.success) setError(res.error || `${action} failed`);
    load();
  };

  const input = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl text-sm';
  const lbl = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';

  /** Said under a field this engine accepts and then ignores. */
  const ignored = (field: string) =>
    (isEnforced(engine, field) ? null : (
      <p className="text-[11px] mt-1" style={{ color: 'var(--amber-label)' }}>{notEnforcedNote(engine)}</p>
    ));

  // One campaign, up close. Rendered instead of the list — the detail view owns
  // the whole surface, including its own queueing.
  if (detailId) {
    return (
      <CampaignDetail
        campaignId={detailId}
        seed={findCampaign(rows, detailId)}
        onBack={() => { setDetailId(null); load(); }}
        onChanged={load}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-[13px]" style={{ color: 'var(--text-muted)' }}>
          Campaigns run on the voice runtime. New ones start as <b>Draft</b> — nothing is dialled until you start them.
        </p>
        <div className="flex gap-2">
          <button onClick={load} className="tl-btn tl-btn-secondary tl-btn-sm">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button onClick={() => setCreating(true)} className="tl-btn tl-btn-primary tl-btn-sm">
            <Plus className="w-3.5 h-3.5" /> New campaign
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-3 py-2 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          {/* This page has a Refresh button of its own, so the banner is not a
              dead end here — but the retry belongs next to the failure rather
              than somewhere else on the page. */}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="underline underline-offset-2 font-medium disabled:opacity-50 whitespace-nowrap"
          >
            {loading ? 'Retrying…' : 'Try again'}
          </button>
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="tl-card flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="tl-card text-center py-10">
          <Megaphone className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="text-sm font-medium text-gray-900 dark:text-white">No voice campaigns yet</p>
          <p className="text-[13px] mt-1" style={{ color: 'var(--text-muted)' }}>
            Create one, queue the numbers to call, then start it.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(c => {
            const id = idOf(c);
            const status = (c.status || 'DRAFT').toUpperCase();
            // The same rules the detail view uses, so a control never appears
            // enabled here and disabled one click deeper.
            const states = lifecycleStates(c.status);
            return (
              <div key={id} className="tl-card">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => setDetailId(id)}
                              className="font-semibold text-gray-900 dark:text-white hover:underline text-left">
                        {c.campaignName || '(unnamed)'}
                      </button>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${badge(status)}`}>{status}</span>
                      {c.callsPerMinute ? <span className="tl-pill">{c.callsPerMinute}/min</span> : null}
                    </div>
                    {c.description && (
                      <div className="text-[12.5px] mt-1" style={{ color: 'var(--text-muted)' }}>{c.description}</div>
                    )}
                    {/* A missing counter shows as an em dash, never as 0 — the
                        engine not counting something is not a count of zero. */}
                    <div className="text-[12px] mt-1 font-mono" style={{ color: 'var(--text-muted)' }}>
                      {formatCounter(c.totalScheduledCalls)} queued · {formatCounter(c.completedCalls)} completed
                      {' '}· {formatCounter(c.failedCalls)} failed
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
                    <button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={() => setRecipientsFor(c)}>
                      <Users className="w-3.5 h-3.5" /> Recipients
                    </button>
                    <button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={() => setDetailId(id)}>
                      <ArrowUpRight className="w-3.5 h-3.5" /> Open
                    </button>
                    {status === 'ACTIVE' ? (
                      <button className="tl-btn tl-btn-secondary tl-btn-sm"
                              disabled={busy === id + 'pause' || !states.pause.enabled}
                              title={states.pause.reason}
                              onClick={() => control(c, 'pause')}>
                        <Pause className="w-3.5 h-3.5" /> Pause
                      </button>
                    ) : status === 'PAUSED' ? (
                      <button className="tl-btn tl-btn-primary tl-btn-sm"
                              disabled={busy === id + 'resume' || !states.resume.enabled}
                              title={states.resume.reason}
                              onClick={() => control(c, 'resume')}>
                        <Play className="w-3.5 h-3.5" /> Resume
                      </button>
                    ) : (
                      <button className="tl-btn tl-btn-primary tl-btn-sm"
                              disabled={busy === id + 'start' || !states.start.enabled}
                              title={states.start.reason}
                              onClick={() => control(c, 'start')}>
                        <Play className="w-3.5 h-3.5" /> Start
                      </button>
                    )}
                  </div>
                </div>
                {/* Why the lifecycle button on this row is dead. Open the
                    campaign for the full set of controls and reasons. */}
                {status !== 'ACTIVE' && status !== 'PAUSED' && !states.start.enabled && states.start.reason && (
                  <p className="text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>{states.start.reason}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'var(--scrim)' }}>
          <div className="tl-card w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-1 text-gray-900 dark:text-white">New voice campaign</h2>
            <p className="text-[12.5px] mb-4" style={{ color: 'var(--text-muted)' }}>
              Created as Draft. Queue recipients first, then start it.
            </p>
            <div className="space-y-3">
              <div>
                <label className={lbl}>Name</label>
                <input className={input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className={lbl}>Description</label>
                <input className={input} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <label className={lbl}>AI agent</label>
                <select className={input} value={form.agent_id} onChange={e => setForm({ ...form, agent_id: e.target.value })}>
                  <option value="">— none —</option>
                  {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  The agent that speaks on the call.
                </p>
              </div>
              <div>
                <label className={lbl}>Caller ID</label>
                {numbers.length > 0 ? (
                  <select className={input} value={form.caller_id}
                          onChange={e => setForm({ ...form, caller_id: e.target.value })}>
                    <option value="">— provider default —</option>
                    {numbers.map(n => (
                      <option key={n.phoneNumber} value={n.phoneNumber}>
                        {n.phoneNumber}{n.friendlyName ? ` — ${n.friendlyName}` : ''}
                        {n.numberType ? ` (${n.numberType})` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <>
                    <input className={input + ' font-mono'} value={form.caller_id}
                           onChange={e => setForm({ ...form, caller_id: e.target.value })}
                           placeholder="+911234567890" />
                    <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                      {numbersNote
                        ? `No number list available — ${numbersNote}. Enter one manually.`
                        : 'No numbers are allocated to this business on the runtime; enter one manually.'}
                    </p>
                  </>
                )}
                {numbers.length > 0 && (
                  <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                    Voice-capable numbers allocated to this business.
                  </p>
                )}
              </div>

              {/* Pacing, dates and the welcome message are stored by every
                  engine and honoured by only some. Each says which it is, read
                  from the engine rather than assumed — a rate limit that does
                  nothing is worse than no rate limit at all. */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Calls per minute</label>
                  <input type="number" min={1} className={input} value={form.calls_per_minute}
                         onChange={e => setForm({ ...form, calls_per_minute: e.target.value })} placeholder="runtime default" />
                  {ignored('calls_per_minute')}
                </div>
                <div>
                  <label className={lbl}>Max concurrent calls</label>
                  <input type="number" min={1} className={input} value={form.max_concurrent_calls}
                         onChange={e => setForm({ ...form, max_concurrent_calls: e.target.value })} placeholder="runtime default" />
                  {ignored('max_concurrent_calls')}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Start date <span style={{ color: 'var(--text-muted)' }}>— optional</span></label>
                  <input type="datetime-local" className={input} value={form.start_date}
                         onChange={e => setForm({ ...form, start_date: e.target.value })} />
                  {ignored('start_date')}
                </div>
                <div>
                  <label className={lbl}>End date <span style={{ color: 'var(--text-muted)' }}>— optional</span></label>
                  <input type="datetime-local" className={input} value={form.end_date}
                         onChange={e => setForm({ ...form, end_date: e.target.value })} />
                  {ignored('end_date')}
                </div>
              </div>

              <div>
                <label className={lbl}>Welcome message <span style={{ color: 'var(--text-muted)' }}>— optional</span></label>
                <input className={input} value={form.welcome_message}
                       onChange={e => setForm({ ...form, welcome_message: e.target.value })} />
                {ignored('welcome_message')}
              </div>

              {traiComplianceOn() && (
                <div>
                  <label className={lbl}>Call category (TRAI) <span style={{ color: 'var(--text-muted)' }}>— optional</span></label>
                  <CallCategorySelect value={category} onChange={setCategory} className={input} />
                  <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                    Recorded and reported only; no call is refused because of it.
                  </p>
                </div>
              )}

              {/* Retry. Collapsed by default: every field left blank keeps the
                  runtime's own default, which is better than anything set here. */}
              <div className="border-t pt-3" style={{ borderColor: 'var(--border)' }}>
                <button type="button" onClick={() => setShowRetry(v => !v)}
                        className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                  {showRetry ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  Retry behaviour
                  <span className="text-[11px] font-normal" style={{ color: 'var(--text-muted)' }}>
                    blank = runtime default
                  </span>
                </button>
                {ignored('retry')}

                {showRetry && (
                  <div className="mt-3 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={lbl}>Max attempts</label>
                        <input type="number" min={1} className={input} value={retry.max_attempts}
                               onChange={e => setRetry({ ...retry, max_attempts: e.target.value })} placeholder="3" />
                      </div>
                      <div>
                        <label className={lbl}>Strategy</label>
                        <select className={input} value={retry.retry_strategy}
                                onChange={e => setRetry({ ...retry, retry_strategy: e.target.value })}>
                          <option value="">— default (Incremental) —</option>
                          <option value="FIXED">Fixed</option>
                          <option value="INCREMENTAL">Incremental</option>
                          <option value="EXPONENTIAL">Exponential</option>
                          <option value="CUSTOM">Custom</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className={lbl}>Base delay (min)</label>
                        <input type="number" min={1} className={input} value={retry.base_retry_delay_minutes}
                               onChange={e => setRetry({ ...retry, base_retry_delay_minutes: e.target.value })} placeholder="15" />
                      </div>
                      <div>
                        <label className={lbl}>Backoff ×</label>
                        <input type="number" step="0.1" min={1} className={input} value={retry.backoff_factor}
                               onChange={e => setRetry({ ...retry, backoff_factor: e.target.value })} placeholder="2.0" />
                      </div>
                      <div>
                        <label className={lbl}>Max delay (min)</label>
                        <input type="number" min={1} className={input} value={retry.max_retry_delay_minutes}
                               onChange={e => setRetry({ ...retry, max_retry_delay_minutes: e.target.value })} placeholder="180" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={lbl}>Max retries per day</label>
                        <input type="number" min={1} className={input} value={retry.max_retries_per_day}
                               onChange={e => setRetry({ ...retry, max_retries_per_day: e.target.value })} placeholder="3" />
                      </div>
                      <div>
                        <label className={lbl}>Jitter (0–1)</label>
                        <input type="number" step="0.05" min={0} max={1} className={input} value={retry.jitter_factor}
                               onChange={e => setRetry({ ...retry, jitter_factor: e.target.value })} placeholder="0.1" />
                      </div>
                    </div>
                    <div>
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked={respectHours ?? true}
                               onChange={e => setRespectHours(e.target.checked)} />
                        Only retry inside business hours
                      </label>
                    </div>
                    <div>
                      <label className={lbl}>After the final failed attempt</label>
                      <select className={input} value={retry.final_failure_action}
                              onChange={e => setRetry({ ...retry, final_failure_action: e.target.value })}>
                        <option value="">— default (Log) —</option>
                        <option value="LOG">Log only</option>
                        <option value="WEBHOOK">Call a webhook</option>
                        <option value="TRANSFER">Transfer to a number</option>
                      </select>
                    </div>
                    {retry.final_failure_action === 'WEBHOOK' && (
                      <div>
                        <label className={lbl}>Failure webhook URL</label>
                        <input className={input + ' font-mono'} value={retry.failure_webhook_url}
                               onChange={e => setRetry({ ...retry, failure_webhook_url: e.target.value })}
                               placeholder="https://…" />
                      </div>
                    )}
                    {retry.final_failure_action === 'TRANSFER' && (
                      <div>
                        <label className={lbl}>Transfer to</label>
                        <input className={input + ' font-mono'} value={retry.failure_transfer_number}
                               onChange={e => setRetry({ ...retry, failure_transfer_number: e.target.value })}
                               placeholder="+911234567890" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button className="tl-btn tl-btn-neutral" onClick={() => setCreating(false)}>Cancel</button>
              <button className="tl-btn tl-btn-primary" onClick={submitCreate} disabled={busy === 'create'}>
                {busy === 'create' ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {recipientsFor && (
        <QueueRecipientsModal
          campaign={recipientsFor}
          onClose={() => setRecipientsFor(null)}
          onQueued={load}
          inputClass={input}
          labelClass={lbl}
        />
      )}

    </div>
  );
};

export default AIVoiceCampaigns;
